import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoaderCircle, Mail, MapPin, Phone, Send } from 'lucide-react';
import { profile } from '@/data/profile';
import { SocialLinks } from '@/components/common/SocialLinks';
import { Button } from '@/components/common/Button';
import { Container, SectionHeading } from '@/components/common/SectionHeading';
import { submitContactForm } from '@/services/contactService';
import { contactSchema, type ContactSchema } from '@/utils/contactSchema';
import styles from './Contact.module.css';

export function Contact() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactSchema>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
      website: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setStatus('idle');
    setStatusMessage('');
    try {
      const result = await submitContactForm(values);
      setStatus(result.ok ? 'success' : 'error');
      setStatusMessage(result.message);
      if (result.ok) {
        reset();
      }
    } catch {
      setStatus('error');
      setStatusMessage('Something went wrong. Please email me directly.');
    }
  });

  return (
    <section id="contact" className="section" aria-labelledby="contact-heading">
      <Container>
        <SectionHeading
          id="contact-heading"
          eyebrow="Contact"
          title="Let’s talk about your next build"
          description="Share a short overview of the problem, timeline, and how I can help."
        />

        <div className={styles.grid}>
          <form className={styles.form} onSubmit={onSubmit} noValidate>
            <div className={styles.honeypot} aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                {...register('website')}
              />
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  {...register('name')}
                />
                {errors.name ? (
                  <p id="name-error" className={styles.error} role="alert">
                    {errors.name.message}
                  </p>
                ) : null}
              </div>

              <div className={styles.field}>
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  {...register('email')}
                />
                {errors.email ? (
                  <p id="email-error" className={styles.error} role="alert">
                    {errors.email.message}
                  </p>
                ) : null}
              </div>
            </div>

            <div className={styles.field}>
              <label htmlFor="subject">Subject</label>
              <input
                id="subject"
                type="text"
                aria-invalid={Boolean(errors.subject)}
                aria-describedby={errors.subject ? 'subject-error' : undefined}
                {...register('subject')}
              />
              {errors.subject ? (
                <p id="subject-error" className={styles.error} role="alert">
                  {errors.subject.message}
                </p>
              ) : null}
            </div>

            <div className={styles.field}>
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                rows={6}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? 'message-error' : undefined}
                {...register('message')}
              />
              {errors.message ? (
                <p id="message-error" className={styles.error} role="alert">
                  {errors.message.message}
                </p>
              ) : null}
            </div>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <LoaderCircle className={styles.spinner} size={18} aria-hidden="true" />
                  Sending…
                </>
              ) : (
                <>
                  <Send size={18} aria-hidden="true" />
                  Send message
                </>
              )}
            </Button>

            <div className={styles.status} aria-live="polite">
              {status !== 'idle' ? (
                <p className={status === 'success' ? styles.success : styles.error}>{statusMessage}</p>
              ) : (
                <p className={styles.hint}>
                  Messages are delivered to {profile.email}. The first submission may require a
                  one-time activation email from FormSubmit.
                </p>
              )}
            </div>
          </form>

          <aside className={styles.info}>
            <div className={styles.infoCard}>
              <p className={styles.infoLabel}>Email</p>
              <a href={`mailto:${profile.email}`} className={styles.infoValue}>
                <Mail size={16} aria-hidden="true" />
                {profile.email}
              </a>
            </div>
            {profile.phonePrimary ? (
              <div className={styles.infoCard}>
                <p className={styles.infoLabel}>Phone</p>
                <a href={`tel:${profile.phonePrimary.replace(/\s+/g, '')}`} className={styles.infoValue}>
                  <Phone size={16} aria-hidden="true" />
                  {profile.phonePrimary}
                </a>
                {profile.phoneSecondary ? (
                  <a
                    href={`tel:${profile.phoneSecondary.replace(/\s+/g, '')}`}
                    className={styles.infoValue}
                  >
                    <Phone size={16} aria-hidden="true" />
                    {profile.phoneSecondary}
                  </a>
                ) : null}
              </div>
            ) : null}
            <div className={styles.infoCard}>
              <p className={styles.infoLabel}>Location</p>
              <p className={styles.infoValue}>
                <MapPin size={16} aria-hidden="true" />
                {profile.location}
              </p>
            </div>
            <SocialLinks />
          </aside>
        </div>
      </Container>
    </section>
  );
}
